import React, { useState, useEffect, useRef } from 'react';
import { adminGetLanguages } from '../../api';

// Loads the content languages once; the default one goes first.
export function useLanguages() {
  const [languages, setLanguages] = useState([]);
  useEffect(() => {
    adminGetLanguages()
      .then(list => setLanguages([...list].sort((a, b) => (b.is_default - a.is_default) || (a.sort_order - b.sort_order))))
      .catch(e => console.error(e));
  }, []);
  const def = languages.find(l => l.is_default);
  return { languages, defLang: def ? def.code : 'en' };
}

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Multi-language editing for one form.
// The form always shows ONE language. Base (default language) values are stored in the
// normal fields; for other languages only the fields that differ are saved into
// `translations[lang]`. Shared fields (numbers, types, coordinates…) are the same for
// every language and are kept in sync when switching tabs.
//
//   capture()      -> current UI state of the form
//   restore(state) -> put a state back into the form
//   toData(state)  -> plain data object, same shape as the DB row
//   fromData(row)  -> UI state built from a DB-shaped object
export function useLangEditor({ defLang, sharedKeys, capture, restore, toData, fromData }) {
  const [lang, setLang] = useState(defLang);
  const drafts = useRef({});        // code -> { state, baseSnapshot }
  const saved = useRef({});         // translations as loaded from the DB

  useEffect(() => { setLang(l => (Object.keys(drafts.current).length ? l : defLang)); }, [defLang]);

  function reset(translations) {
    drafts.current = {};
    saved.current = { ...(translations || {}) };
    setLang(defLang);
  }

  function switchTo(code) {
    if (code === lang) return;
    const cur = capture();
    drafts.current[lang] = { ...(drafts.current[lang] || {}), state: cur };
    const curData = toData(cur);

    let target = drafts.current[code];
    if (!target) {
      const baseData = toData(drafts.current[defLang] ? drafts.current[defLang].state : cur);
      const merged = { ...baseData, ...(saved.current[code] || {}) };
      target = { state: fromData(merged), baseSnapshot: baseData };
      drafts.current[code] = target;
    }
    // shared fields follow whatever was just set on the previous tab
    const synced = { ...toData(target.state) };
    for (const k of sharedKeys) if (k in curData) synced[k] = curData[k];
    const next = fromData(synced);
    drafts.current[code] = { ...target, state: next };
    restore(next);
    setLang(code);
  }

  // Returns { base, translations } ready to send to the server.
  function collect() {
    const all = { ...drafts.current, [lang]: { ...(drafts.current[lang] || {}), state: capture() } };
    const base = toData(all[defLang].state);
    const translations = { ...saved.current };
    for (const code of Object.keys(all)) {
      if (code === defLang) continue;
      const d = toData(all[code].state);
      const snap = all[code].baseSnapshot || base;
      const override = {};
      for (const k of Object.keys(d)) {
        if (k === 'translations' || sharedKeys.includes(k)) continue;
        if (!same(d[k], snap[k])) override[k] = d[k];
      }
      if (Object.keys(override).length) translations[code] = override;
      else delete translations[code];
    }
    return { base, translations };
  }

  return { lang, switchTo, reset, collect, isDefault: lang === defLang };
}

// Tabs above a form: EN (основной) | AR | RU …  A dot marks languages that already have a translation.
export function LangBar({ languages, lang, onSwitch, translations }) {
  if (!languages || languages.length < 2) return null;
  const current = languages.find(l => l.code === lang);
  return (
    <div className="admin-langbar-wrap">
      <div className="admin-langbar">
        <span className="admin-langbar-label">Язык:</span>
        {languages.filter(l => l.is_active || l.code === lang).map(l => {
          const has = !l.is_default && translations && translations[l.code] && Object.keys(translations[l.code]).length > 0;
          return (
            <button key={l.code} type="button"
              className={`admin-lang-tab ${l.code === lang ? 'active' : ''}`}
              onClick={() => onSwitch(l.code)}
              title={l.name}>
              {l.code.toUpperCase()}{l.is_default ? ' · основной' : ''}{has ? ' •' : ''}
            </button>
          );
        })}
      </div>
      {current && !current.is_default && (
        <div className="admin-lang-note" dir="auto">
          Вы редактируете версию на языке <b>{current.name}</b>. Меняйте тексты, аудио, картинки и варианты ответов.
          Номер, тип задания, координаты и баллы общие для всех языков.
          Что не переведено, игрок увидит на основном языке.
        </div>
      )}
    </div>
  );
}
